import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getAccommodationAttributesByGroup } from '../../common/api/accommodationAttributesService';
import { RootState } from '../../app/store';
import { LOCALES } from '../../common/constants';

export enum AttributeGroupOptions {
  None = 'None',
  Equipment = 'Equipment',
  Information = 'Information',
  Distance = 'Distance',
  Other = 'Other',
}

interface MultiSelectOption {
  optionId: string;
  name: string;
}

export interface AccommodationAttribute {
  attributeId: string;
  typeName: string;
  name: string;
  multiSelectOptions?: MultiSelectOption[];
}

interface AttributesState {
  loading: boolean;
  error: string | null;
  equipmentAttributes: AccommodationAttribute[];
  distanceAttributes: AccommodationAttribute[];
}

const initialState: AttributesState = {
  loading: false,
  error: null,
  equipmentAttributes: [],
  distanceAttributes: [],
};

export const fetchAttributes = createAsyncThunk(
  'attributes/fetchAttributes',
  async (locale: LOCALES, { rejectWithValue }) => {
    try {
      const equipmentAttributes = await getAccommodationAttributesByGroup(
        AttributeGroupOptions.Equipment,
        locale,
      );
      const distanceAttributes = await getAccommodationAttributesByGroup(
        AttributeGroupOptions.Distance,
        locale,
      );
      const informationAttributes = await getAccommodationAttributesByGroup(
        AttributeGroupOptions.Information,
        locale,
      );
      return { equipmentAttributes, distanceAttributes, informationAttributes };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

const attributesSlice = createSlice({
  name: 'attributes',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchAttributes.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAttributes.fulfilled, (state, action) => {
        state.loading = false;
        state.equipmentAttributes = [
          ...action.payload.equipmentAttributes,
          ...action.payload.informationAttributes,
        ];
        state.distanceAttributes = action.payload.distanceAttributes;
      })
      .addCase(fetchAttributes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const selectEquipmentAttributes = (state: RootState) =>
  state.attributes.equipmentAttributes;
export const selectDistanceAttributes = (state: RootState) =>
  state.attributes.distanceAttributes;

export default attributesSlice.reducer;
